/**
 * URL-synced list state codec — copied verbatim from ptc-core CRM (web/src/components/
 * useListSearchParams.ts), the shared grammar for the unified-list-filters program.
 *
 * List routes carry
 * `?view=<key>&filter.<col>.<op>=<value>&sort=<col>&dir=<asc|desc>&page=<n>&pageSize=<n>`.
 *
 * This hook owns parse/serialize of `view`, `sort`+`dir`, `page`, `pageSize`, and all
 * `filter.*` keys. Params it doesn't own pass through untouched. Conventions:
 *
 * - **Defaults are omitted** — page 1, pageSize {@link DEFAULT_PAGE_SIZE}, no sort, and the
 *   default view never appear as params; absent `view` = the default view.
 * - **Precedence: URL wins** — `selectView` writes `view=<key>` and clears every override
 *   param (clean slate = the view's own definition); subsequent tweaks write explicit params
 *   on top.
 * - **Page hygiene** — any filter/sort/pageSize/view change removes `page` (back to 1).
 * - **History replace** — mutations replace (never push) so twiddling doesn't bloat Back.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, ViewSort } from './viewDefinition';
import { GROUP_GRANULARITIES, type GroupGranularity } from './groupBy';

const FILTER_PREFIX = 'filter.';
/** The non-filter params this hook owns (view is handled separately). */
const OVERRIDE_SCALARS = ['sort', 'dir', 'page', 'pageSize', 'group', 'groupBy'] as const;

/** Raw URL list state. `view` is the EFFECTIVE key (absent param ⇒ the default view). */
export interface ListSearchState {
  view: string;
  /** The literal `view` param, `null` when absent (i.e. the default view is active). */
  rawView: string | null;
  /** URL filter overrides, keyed `filter.<col>.<op>` (empty values kept — data ops). */
  filters: Record<string, string>;
  /** Explicit sort override; `null` = defer to the view definition / endpoint default. */
  sort: ViewSort | null;
  page: number;
  /** Explicit pageSize override; `null` = defer to view definition / prop / default. */
  pageSize: number | null;
  /** Active group-by column key (fleet filter contract v3 §13, M1); `null` when not grouped. */
  group: string | null;
  /** Active group granularity; non-null only when `group` is also non-null. */
  groupGranularity: GroupGranularity | null;
}

export interface ListSearchParams extends ListSearchState {
  /** Replace ALL `filter.*` params with `next` (resets page). */
  setFilters: (next: Record<string, string>) => void;
  /** Set or clear the explicit sort override (resets page). */
  setSort: (sort: ViewSort | null) => void;
  /** Navigate; 1 (the default) removes the param. */
  setPage: (page: number) => void;
  /** Set the pageSize override (resets page). Selecting `omitWhen` (default
   * {@link DEFAULT_PAGE_SIZE}) removes the param. */
  setPageSize: (pageSize: number, omitWhen?: number) => void;
  /** Activate a view: clears every override param; the default view key omits `view=`. */
  selectView: (key: string) => void;
  /** Group by `col` at `granularity` (M1: `group=<col>&groupBy=<gran>`). Does not touch `page` --
   * pagination is disabled while grouped, per DataTable's own grouping contract. */
  setGroup: (col: string, granularity: GroupGranularity) => void;
  /** Clear grouping entirely. */
  clearGroup: () => void;
}

/** Remove every param this hook owns except `view` (the "clean slate"). */
function clearOverrides(p: URLSearchParams): void {
  for (const key of [...p.keys()]) {
    if (key.startsWith(FILTER_PREFIX)) p.delete(key);
  }
  for (const key of OVERRIDE_SCALARS) p.delete(key);
}

function parsePositiveInt(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

export function useListSearchParams(defaultViewKey: string): ListSearchParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<ListSearchState>(() => {
    const filters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith(FILTER_PREFIX)) filters[key] = value;
    });

    const sortCol = searchParams.get('sort');
    const rawView = searchParams.get('view');
    const groupCol = searchParams.get('group');
    const groupByRaw = searchParams.get('groupBy');
    const validGranularity = (GROUP_GRANULARITIES as readonly string[]).includes(groupByRaw ?? '')
      ? (groupByRaw as GroupGranularity)
      : null;
    return {
      view: rawView ?? defaultViewKey,
      rawView,
      filters,
      sort: sortCol ? { col: sortCol, dir: searchParams.get('dir') === 'desc' ? 'desc' : 'asc' } : null,
      page: parsePositiveInt(searchParams.get('page')) ?? 1,
      pageSize: parsePositiveInt(searchParams.get('pageSize')),
      group: groupCol && validGranularity ? groupCol : null,
      groupGranularity: groupCol && validGranularity ? validGranularity : null,
    };
  }, [searchParams, defaultViewKey]);

  /** All mutations replace history (never push) and go through the functional form. */
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setFilters = useCallback(
    (nextFilters: Record<string, string>) => {
      update((next) => {
        for (const key of [...next.keys()]) {
          if (key.startsWith(FILTER_PREFIX)) next.delete(key);
        }
        for (const [key, value] of Object.entries(nextFilters)) next.set(key, value);
        next.delete('page');
      });
    },
    [update]
  );

  const setSort = useCallback(
    (sort: ViewSort | null) => {
      update((next) => {
        if (sort) {
          next.set('sort', sort.col);
          next.set('dir', sort.dir);
        } else {
          next.delete('sort');
          next.delete('dir');
        }
        next.delete('page');
      });
    },
    [update]
  );

  const setPage = useCallback(
    (page: number) => {
      update((next) => {
        if (page > 1) next.set('page', String(page));
        else next.delete('page');
      });
    },
    [update]
  );

  const setPageSize = useCallback(
    (pageSize: number, omitWhen: number = DEFAULT_PAGE_SIZE) => {
      update((next) => {
        if (pageSize === omitWhen) next.delete('pageSize');
        else next.set('pageSize', String(pageSize));
        next.delete('page');
      });
    },
    [update]
  );

  const selectView = useCallback(
    (key: string) => {
      update((next) => {
        clearOverrides(next);
        if (key === defaultViewKey) next.delete('view');
        else next.set('view', key);
      });
    },
    [update, defaultViewKey]
  );

  const setGroup = useCallback(
    (col: string, granularity: GroupGranularity) => {
      update((next) => {
        next.set('group', col);
        next.set('groupBy', granularity);
      });
    },
    [update]
  );

  const clearGroup = useCallback(() => {
    update((next) => {
      next.delete('group');
      next.delete('groupBy');
    });
  }, [update]);

  return { ...state, setFilters, setSort, setPage, setPageSize, selectView, setGroup, clearGroup };
}
